import {
    InstructionHubBlockedKeys,
    IsInstructionHubBlockedTopicCellParams,
    InstructionHubBlockedTargets
} from '@containers/activity-router/routes/helpers/instruction-hub/instruction-hub.types';
import {
    getActivityKey,
    getTopicActivityKey
} from '@containers/activity-router/routes/helpers/keys/key-generator';
import {
    ActivityId,
    StudentId,
    TopicId
} from '@containers/activity-router/routes/helpers/slice/slice.types';
import {
    GridCells,
    GridCounters
} from '@containers/activity-router/routes/helpers/slice/topic/topic-slice.types';
import {
    CombinedAssignActivities,
    CombinedAssignSelectedCourse
} from '@services/gateways/combined-assign-gateway/combined-assign-gateway.types';
import {
    CombinedAssignInstructionHubRecord,
    getCombinedAssignInstructionHubState,
    isInstructionHubBlocked
} from '@services/instruction-hub/instruction-hub-helpers';
import { InstructionHubFlags } from '@services/instruction-hub/instruction-hub.types';

export type IsInstructionHubBlockedAssignmentParams = {
    instructionHubBlockedKeys?: InstructionHubBlockedKeys
    studentKey: StudentId
    activityKey: ActivityId
};

export function isInstructionHubBlockedAssignment ({
    instructionHubBlockedKeys,
    studentKey,
    activityKey
}: IsInstructionHubBlockedAssignmentParams) {
    return instructionHubBlockedKeys?.[studentKey]?.[activityKey] === true;
}

// A collapsed topic cell is IH-blocked for a student when every activity in the topic is IH-owned for them.
export function isInstructionHubBlockedTopicCell ({
    gridCells,
    instructionHubBlockedKeys,
    studentKey,
    topicKey
}: IsInstructionHubBlockedTopicCellParams) {
    const activityKeys = Object.keys(gridCells[studentKey]?.[topicKey] ?? {});

    return activityKeys.length > 0 && activityKeys.every((activityKey) => isInstructionHubBlockedAssignment({
        instructionHubBlockedKeys,
        studentKey,
        activityKey
    }));
}

/**
 * True when Instruction Hub is on and at least one current cell is IH-owned.
 * Blocked keys are only built while Instruction Hub is on, so they carry both conditions.
 */
export function hasInstructionHubAssignments (instructionHubBlockedKeys?: InstructionHubBlockedKeys) {
    return Object.values(instructionHubBlockedKeys ?? {}).some((activityKeys) => Object.values(activityKeys).some(Boolean));
}

export function getCourseActivityKeys (course: Pick<CombinedAssignSelectedCourse, 'topics'> | null): Record<ActivityId, string> {
    const activityKeys: Record<ActivityId, string> = {};

    function addActivityKeys (activities: Array<CombinedAssignActivities> | null) {
        activities?.forEach(({ id, activityType }) => {
            activityKeys[id] = getActivityKey({
                id,
                activityType
            });
        });
    }

    course?.topics.forEach((strand) => {
        addActivityKeys(strand.activities);
        strand.topics?.forEach((topic) => addActivityKeys(topic.activities));
    });

    return activityKeys;
}

export function getStudentAssignmentsBlockedKeys<TAssignment extends CombinedAssignInstructionHubRecord> (
    studentAssignments: Record<StudentId, { assignments: TAssignment[] }>,
    getAssignmentActivityKey: (assignment: TAssignment) => string | undefined,
    instructionHubFlags: InstructionHubFlags
): InstructionHubBlockedKeys {
    const blockedKeys: InstructionHubBlockedKeys = {};

    Object.keys(studentAssignments).forEach((studentId) => {
        studentAssignments[studentId]?.assignments.forEach((assignment) => {
            const activityKey = getAssignmentActivityKey(assignment);

            if (!activityKey || !isInstructionHubBlocked(getCombinedAssignInstructionHubState(assignment), instructionHubFlags)) return;

            blockedKeys[studentId] ??= {};
            blockedKeys[studentId][activityKey] = true;
        });
    });

    return blockedKeys;
}

type GetInstructionHubBlockedTargetsParams = {
    gridCells: GridCells
    gridCounters: GridCounters
    instructionHubBlockedKeys?: InstructionHubBlockedKeys
};

function isFullyBlocked (blockedCount: number, totalActivities: number) {
    return totalActivities > 0 && blockedCount === totalActivities;
}

export function getInstructionHubBlockedTargets ({
    gridCells,
    gridCounters,
    instructionHubBlockedKeys
}: GetInstructionHubBlockedTargetsParams): InstructionHubBlockedTargets {
    if (!instructionHubBlockedKeys || Object.keys(instructionHubBlockedKeys).length === 0) {
        return {
            topicKeys: new Set(),
            activityKeys: new Set(),
            studentIds: new Set()
        };
    }

    const blockedCountsByStudent: Record<StudentId, number> = {};
    const blockedCountsByTopic: Record<TopicId, number> = {};
    const blockedCountsByActivity: Record<TopicId, Record<ActivityId, number>> = {};

    Object.keys(instructionHubBlockedKeys).forEach((studentKey) => {
        const studentCells = gridCells[studentKey];

        if (!studentCells) return;

        Object.keys(studentCells).forEach((topicKey) => {
            Object.keys(studentCells[topicKey]).forEach((activityKey) => {
                if (instructionHubBlockedKeys[studentKey][activityKey] !== true) return;

                blockedCountsByStudent[studentKey] = (blockedCountsByStudent[studentKey] || 0) + 1;
                blockedCountsByTopic[topicKey] = (blockedCountsByTopic[topicKey] || 0) + 1;
                blockedCountsByActivity[topicKey] = blockedCountsByActivity[topicKey] || {};
                blockedCountsByActivity[topicKey][activityKey] = (blockedCountsByActivity[topicKey][activityKey] || 0) + 1;
            });
        });
    });

    const studentIds = new Set<StudentId>();

    Object.keys(blockedCountsByStudent).forEach((studentId) => {
        const totalActivities = gridCounters.studentCount[studentId]?.totalActivities ?? 0;

        if (isFullyBlocked(blockedCountsByStudent[studentId], totalActivities)) studentIds.add(studentId);
    });

    const topicKeys = new Set<TopicId>();

    Object.keys(blockedCountsByTopic).forEach((topicKey) => {
        const totalActivities = gridCounters.topicCount[topicKey]?.totalActivities ?? 0;

        if (isFullyBlocked(blockedCountsByTopic[topicKey], totalActivities)) topicKeys.add(topicKey);
    });

    const activityKeys = new Set<string>();

    Object.keys(blockedCountsByActivity).forEach((topicKey) => {
        Object.keys(blockedCountsByActivity[topicKey]).forEach((activityKey) => {
            const totalActivities = gridCounters.activityCount[topicKey]?.[activityKey]?.totalActivities ?? 0;

            if (isFullyBlocked(blockedCountsByActivity[topicKey][activityKey], totalActivities)) {
                activityKeys.add(getTopicActivityKey({
                    topicKey,
                    activityKey
                }));
            }
        });
    });

    return {
        topicKeys,
        activityKeys,
        studentIds
    };
}
